/**
 * DocumentAgentLGContext
 *
 * Context manager for TipTap-based document agent.
 * Maintains the TipTap document workspace state.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { JSONContent } from "@tiptap/react";
import { v4 as uuidv4 } from "uuid";
import {
  DocumentContextData,
  DocumentSnapshot,
  TipTapDocument,
  PDFDocument,
  FormMutableDocument,
  FormImmutableDocument,
  DocumentType,
  DocumentComment,
  DocumentTrackChangesState,
  TipTapPatch,
  SerializedTipTapNode,
} from "./DocumentAgentLGTypes";
import { TipTapEditorRef } from "../../Components/TipTapEditor/TipTapEditor";
import {
  OperationPatch,
  PatchExecutionResult,
  OperationCommentData as CommentData,
} from "../../Components/TipTapEditor/operations";
import { handleFetchUnauthorized } from "../../Utils/handleFetchUnauthorized";

// ============================================================================
// TYPES
// ============================================================================

interface DocumentContextProviderProps {
  workproductId: string;
  workproductType: string;
  /** When true, documents are view-only (chat remains active) */
  documentReadOnly?: boolean;
  children: React.ReactNode;
}

interface DocumentContextValue {
  documentContext: DocumentContextData;
  /** Whether documents should be read-only (view-only mode for reference docs) */
  documentReadOnly: boolean;

  // TipTap document methods
  markDocumentDirty: (
    docId: string,
    content: JSONContent,
    documentName?: string
  ) => void;
  markDocumentClean: (
    docId: string,
    savedContent?: JSONContent,
    savedVersion?: number
  ) => void;
  updateDocumentContent: (
    docId: string,
    jsonContent: JSONContent,
    htmlContent?: string,
    documentName?: string,
    isToolUpdate?: boolean
  ) => void;
  addTipTapDocument: (
    docId: string,
    documentName: string,
    content: JSONContent,
    editable?: boolean,
    gcsPath?: string,
    meta?: Record<string, any>,
    htmlContent?: string,
    version?: number,
    comments?: DocumentComment[]
  ) => void;

  // PDF methods
  addPDFDocument: (
    docId: string,
    documentName: string,
    pdfGcsPath: string,
    ocrGcsPath: string,
    ocrMetadata: PDFDocument["ocrMetadata"],
    signedPdfUrl?: string
  ) => void;
  processPDFUpload: (file: File, documentName?: string) => Promise<string>;

  // Form methods
  addFormDocument: (
    docId: string,
    documentName: string,
    formData: Record<string, any>,
    isMutable: boolean,
    gcsPath?: string,
    formSchema?: string,
    initialDirty?: boolean,
    meta?: Record<string, any>
  ) => void;
  updateFormData: (docId: string, formData: Record<string, any>) => void;

  // General methods
  getContextForAgent: () => DocumentContextData;
  isAnyDocumentDirty: () => boolean;
  getDocumentById: (docId: string) => DocumentSnapshot | undefined;
  loadDocumentById: (docId: string) => Promise<boolean>;
  getAvailableDocuments: () => Array<{
    docId: string;
    documentName: string;
    documentType: DocumentType;
  }>;
  renameDocument: (docId: string, newName: string) => Promise<boolean>;
  removeDocument: (docId: string) => Promise<boolean>;

  // UI state
  setSelectedDocument: (docId: string | null) => void;
  setVisibleDocuments: (docIds: string[]) => void;

  // Track changes
  setTrackChangesState: (
    docId: string,
    state: DocumentTrackChangesState | null
  ) => void;
  getTrackChangesState: (docId: string) => DocumentTrackChangesState | null;
  updateTrackChangesState: (
    docId: string,
    updates: Partial<DocumentTrackChangesState>
  ) => void;
  clearTrackChangesData: (docId: string) => void;

  // Unsaved editor state
  saveUnsavedEditorState: (
    docId: string,
    json: JSONContent,
    html: string
  ) => void;
  restoreUnsavedEditorState: (
    docId: string
  ) => { json: JSONContent; html: string } | null;
  clearUnsavedEditorState: (docId: string) => void;

  // Persistence
  saveDirtyDocuments: () => Promise<{ saved: string[]; failed: string[] }>;

  // Save-conflict (block-and-notify): docs whose save was rejected as stale
  // (409). Autosaves are blocked for these; the pane surfaces a reload prompt.
  conflictedDocIds: Set<string>;
  markDocumentConflicted: (docId: string) => void;
  clearDocumentConflict: (docId: string) => void;

  // Editor ref management (for patch application)
  registerEditor: (
    docId: string,
    ref: React.RefObject<TipTapEditorRef>
  ) => void;
  unregisterEditor: (docId: string) => void;
  applyPatch: (patch: TipTapPatch) => PatchExecutionResult | null;
  getSerializedDocument: (docId: string) => SerializedTipTapNode[] | null;

  // Comment sync (for persisting comments from editor state to context).
  // markDirty=false is used when syncing freshly-loaded comments as a clean
  // baseline (so loading a document with comments does not flip isDirty).
  updateDocumentComments: (
    docId: string,
    comments: DocumentComment[],
    markDirty?: boolean
  ) => void;

  // Comment callback management (for syncing AI-added comments to UI)
  registerCommentCallback: (
    docId: string,
    callback: (comments: CommentData[]) => void
  ) => void;
  unregisterCommentCallback: (docId: string) => void;

  // Agent working state (used to pause auto-save and lock editor)
  isAgentWorking: boolean;
  setAgentWorking: (working: boolean) => void;
}

const DocumentContext = createContext<DocumentContextValue | null>(null);

export const useDocumentContext = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error(
      "useDocumentContext must be used within DocumentContextProvider"
    );
  }
  return context;
};

// ============================================================================
// CONSTANTS
// ============================================================================

const CONTENT_SIZE_THRESHOLD = 10000;

// ============================================================================
// AUTO-SAVE HELPER
// ============================================================================

/**
 * Persist an agent tool update. Returns the version the server assigned
 * (so the caller can adopt it into context state), or null on failure.
 * Handles one 409 stale-version round trip by adopting the server's
 * current version and retrying.
 */
type ToolAutoSaveResult =
  | { status: "ok"; version: number }
  | { status: "conflict"; serverVersion: number }
  | { status: "error" };

async function autoSaveToolUpdate(
  workproductId: string,
  docId: string,
  documentName: string,
  jsonContent: JSONContent,
  htmlContent: string,
  currentVersion: number,
  comments?: DocumentComment[]
): Promise<ToolAutoSaveResult> {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  const attempt = (version: number) =>
    fetch(`${backendUrl}/api/document-agent/save-document`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        workproductId,
        docId,
        documentName,
        documentType: "tiptap",
        document: {
          json: jsonContent,
          html: htmlContent,
          version,
          updated: new Date().toISOString(),
          comments,
        },
        changesSummary: "Auto-saved after agent update",
        meta: { documentName },
      }),
      keepalive: true,
    });

  try {
    const resp = await attempt((currentVersion || 0) + 1);
    if (resp.status === 409) {
      // BLOCK & NOTIFY: the server has a newer copy. Do NOT re-send with a
      // bumped version — that silently overwrote the newer content. Report the
      // conflict so the caller can flag the doc and prompt a reload/merge.
      const conflict = await resp.json().catch(() => null);
      const serverVersion = conflict?.currentVersion;
      return {
        status: "conflict",
        serverVersion:
          typeof serverVersion === "number"
            ? serverVersion
            : currentVersion || 0,
      };
    }
    if (resp.ok) {
      const respJson = await resp.json().catch(() => null);
      const v = respJson?.documentMeta?.version;
      return {
        status: "ok",
        version: typeof v === "number" ? v : (currentVersion || 0) + 1,
      };
    }
    return { status: "error" };
  } catch (err) {
    return { status: "error" };
  }
}

// ============================================================================
// PROVIDER
// ============================================================================

export const DocumentContextProvider: React.FC<DocumentContextProviderProps> = ({
  workproductId,
  workproductType,
  documentReadOnly = false,
  children,
}) => {
  const initialContext: DocumentContextData = {
    workproductId,
    documents: {},
    metadata: {},
  };

  const [documentContext, setDocumentContext] =
    useState<DocumentContextData>(initialContext);
  const documentContextRef = useRef<DocumentContextData>(initialContext);
  const editorRefs = useRef<Record<string, React.RefObject<TipTapEditorRef>>>(
    {}
  );
  const commentCallbacks = useRef<
    Record<string, (comments: CommentData[]) => void>
  >({});

  // Queue for patches targeting non-active (unmounted) editors.
  // When the editor mounts, it drains this queue.
  const pendingPatches = useRef<Record<string, TipTapPatch[]>>({});

  // Agent working state — used to pause auto-save and lock editor during agent operations
  const [isAgentWorking, setIsAgentWorking] = useState(false);
  const setAgentWorking = useCallback((working: boolean) => {
    setIsAgentWorking(working);
  }, []);

  // Documents whose last save hit a 409 version conflict — the server has a
  // newer copy than this client believed. We BLOCK further autosaves for these
  // (never silently overwrite the newer server copy) and surface a reload
  // prompt in the pane so the user can merge (block-and-notify).
  const [conflictedDocIds, setConflictedDocIds] = useState<Set<string>>(
    () => new Set()
  );
  const conflictedDocIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    conflictedDocIdsRef.current = conflictedDocIds;
  }, [conflictedDocIds]);
  const markDocumentConflicted = useCallback((docId: string) => {
    setConflictedDocIds(prev => {
      if (prev.has(docId)) return prev;
      const next = new Set(prev);
      next.add(docId);
      return next;
    });
  }, []);
  const clearDocumentConflict = useCallback((docId: string) => {
    setConflictedDocIds(prev => {
      if (!prev.has(docId)) return prev;
      const next = new Set(prev);
      next.delete(docId);
      return next;
    });
  }, []);

  // Helper to synchronously update both state and ref
  const updateContext = useCallback(
    (updater: (prev: DocumentContextData) => DocumentContextData) => {
      setDocumentContext(prev => {
        const next = updater(prev);
        documentContextRef.current = next;
        return next;
      });
    },
    []
  );

  // ============================================================================
  // EDITOR REF MANAGEMENT
  // ============================================================================

  const registerEditor = useCallback(
    (docId: string, ref: React.RefObject<TipTapEditorRef>) => {
      editorRefs.current[docId] = ref;

      // Drain any patches queued while this editor was unmounted
      const queued = pendingPatches.current[docId];
      if (queued && queued.length > 0) {
        // Apply after a short delay to ensure the editor is fully initialized
        setTimeout(() => {
          for (const patch of queued) {
            const editorRef = editorRefs.current[docId];
            if (!editorRef?.current) break;

            // Enable track changes and set AI author for queued patches
            if (!editorRef.current.isTrackChangesEnabled()) {
              editorRef.current.enableTrackChanges();
            }
            const prevAuthor = editorRef.current.getTrackChangesAuthor();
            editorRef.current.setTrackChangesAuthor("AI Agent");

            const operationPatch: OperationPatch = {
              operations: patch.operations,
            };
            const result = editorRef.current.applyOperations(operationPatch);

            // Restore previous author
            if (prevAuthor) {
              editorRef.current.setTrackChangesAuthor(prevAuthor);
            }
            if (result.success) {
              const json = editorRef.current.getJSON();
              const html = editorRef.current.getHTML();
              updateContext(prev => {
                const doc = prev.documents[docId];
                if (!doc || doc.documentType !== "tiptap") return prev;
                const textLength = editorRef.current?.getText().length || 0;
                return {
                  ...prev,
                  documents: {
                    ...prev.documents,
                    [docId]: {
                      ...doc,
                      content: json,
                      htmlContent: html,
                      isDirty: true,
                      isLarge: textLength > CONTENT_SIZE_THRESHOLD,
                      lastModified: new Date(),
                    },
                  },
                };
              });
            }
          }
          delete pendingPatches.current[docId];
        }, 100);
      }
    },
    [updateContext]
  );

  const unregisterEditor = useCallback((docId: string) => {
    delete editorRefs.current[docId];
  }, []);

  // ============================================================================
  // COMMENT CALLBACK MANAGEMENT
  // ============================================================================

  const registerCommentCallback = useCallback(
    (docId: string, callback: (comments: CommentData[]) => void) => {
      commentCallbacks.current[docId] = callback;
    },
    []
  );

  const unregisterCommentCallback = useCallback((docId: string) => {
    delete commentCallbacks.current[docId];
  }, []);

  const applyPatch = useCallback(
    (patch: TipTapPatch): PatchExecutionResult | null => {
      const editorRef = editorRefs.current[patch.documentId];
      if (!editorRef?.current) {
        // Editor not mounted (non-active tab). Queue for later.
        if (!pendingPatches.current[patch.documentId]) {
          pendingPatches.current[patch.documentId] = [];
        }
        pendingPatches.current[patch.documentId].push(patch);
        // Return a synthetic success so the agent doesn't think it failed
        return {
          success: true,
          results: patch.operations.map((_, i) => ({
            success: true,
            operationIndex: i,
          })),
          failedCount: 0,
          comments: [],
        };
      }

      // Enable track changes before applying AI operations so that
      // insertions / deletions are recorded as tracked changes the user
      // can review, accept, or reject.
      if (!editorRef.current.isTrackChangesEnabled()) {
        editorRef.current.enableTrackChanges();
      }

      // Set the author to "AI Agent" so track change marks are attributed
      // correctly (instead of showing "Unknown").  Save the previous author
      // so we can restore it after the AI operations (otherwise subsequent
      // user edits would also be attributed to "AI Agent").
      const previousAuthor = editorRef.current.getTrackChangesAuthor();
      editorRef.current.setTrackChangesAuthor("AI Agent");

      const operationPatch: OperationPatch = { operations: patch.operations };
      const result = editorRef.current.applyOperations(operationPatch);

      // Restore the previous author for user edits
      if (previousAuthor) {
        editorRef.current.setTrackChangesAuthor(previousAuthor);
      }

      if (result.success) {
        // Get updated content and mark dirty
        const json = editorRef.current.getJSON();
        const html = editorRef.current.getHTML();

        updateContext(prev => {
          const doc = prev.documents[patch.documentId];
          if (!doc || doc.documentType !== "tiptap") return prev;

          const textLength = editorRef.current?.getText().length || 0;
          const updatedDoc: TipTapDocument = {
            ...doc,
            content: json,
            htmlContent: html,
            isDirty: true,
            isLarge: textLength > CONTENT_SIZE_THRESHOLD,
            lastModified: new Date(),
          };

          return {
            ...prev,
            documents: { ...prev.documents, [patch.documentId]: updatedDoc },
          };
        });

        // If there are comments returned from the operations, notify the registered callback
        if (result.comments && result.comments.length > 0) {
          const callback = commentCallbacks.current[patch.documentId];
          if (callback) {
            callback(result.comments);
          }
        }
      }

      return result;
    },
    [updateContext]
  );

  const getSerializedDocument = useCallback(
    (docId: string): SerializedTipTapNode[] | null => {
      const editorRef = editorRefs.current[docId];
      if (!editorRef?.current) return null;
      return editorRef.current.getDocumentForLLM();
    },
    []
  );

  // ============================================================================
  // UI STATE
  // ============================================================================

  const setSelectedDocument = useCallback(
    (docId: string | null) => {
      updateContext(prev => ({
        ...prev,
        metadata: {
          ...(prev.metadata || {}),
          selectedDocId: docId || undefined,
          selectedDocIds: docId ? [docId] : [],
        },
      }));
    },
    [updateContext]
  );

  const setVisibleDocuments = useCallback(
    (docIds: string[]) => {
      updateContext(prev => ({
        ...prev,
        metadata: { ...(prev.metadata || {}), selectedDocIds: docIds || [] },
      }));
    },
    [updateContext]
  );

  // ============================================================================
  // TIPTAP DOCUMENT METHODS
  // ============================================================================

  const markDocumentDirty = useCallback(
    (docId: string, content: JSONContent, documentName?: string) => {
      updateContext(prev => {
        const existingDoc = prev.documents[docId];
        if (existingDoc && existingDoc.documentType !== "tiptap") return prev;

        const textLength = JSON.stringify(content).length;
        const tiptapDoc: TipTapDocument = {
          id: docId,
          documentType: "tiptap",
          documentName:
            documentName || existingDoc?.documentName || "Unknown Document",
          content,
          isDirty: true,
          isLarge: textLength > CONTENT_SIZE_THRESHOLD,
          lastModified: new Date(),
          version: existingDoc?.version || 1,
          ...(existingDoc?.documentType === "tiptap"
            ? { unsavedEditorState: existingDoc.unsavedEditorState }
            : {}),
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: tiptapDoc },
        };
      });
    },
    [updateContext]
  );

  const markDocumentClean = useCallback(
    (docId: string, savedContent?: JSONContent, savedVersion?: number) => {
      updateContext(prev => {
        const existingDoc = prev.documents[docId];
        if (!existingDoc || existingDoc.documentType !== "tiptap") return prev;

        const cleanDoc: TipTapDocument = {
          ...existingDoc,
          content: savedContent || existingDoc.content,
          isDirty: false,
          lastModified: new Date(),
          // Adopt the version the server just assigned. Without this, every
          // later save from this context sends the same stale version and is
          // rejected 409 by the server's stale-write guard.
          ...(typeof savedVersion === "number" &&
          savedVersion > (existingDoc.version || 0)
            ? { version: savedVersion }
            : {}),
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: cleanDoc },
        };
      });
    },
    [updateContext]
  );

  // Bump a tiptap document's version to what the server reports, without
  // touching dirty state (used by background savers whose save may be older
  // than the user's in-flight edits).
  const adoptServerVersion = useCallback(
    (docId: string, version: number) => {
      updateContext(prev => {
        const doc = prev.documents[docId];
        if (!doc || doc.documentType !== "tiptap") return prev;
        if ((doc.version || 0) >= version) return prev;
        return {
          ...prev,
          documents: { ...prev.documents, [docId]: { ...doc, version } },
        };
      });
    },
    [updateContext]
  );

  const updateDocumentContent = useCallback(
    (
      docId: string,
      jsonContent: JSONContent,
      htmlContent?: string,
      documentName?: string,
      isToolUpdate?: boolean
    ) => {
      const currentDocRef = documentContextRef.current.documents[docId];
      const actualIsToolUpdate = isToolUpdate === true;
      const hasChangedRef =
        currentDocRef?.documentType === "tiptap"
          ? JSON.stringify(currentDocRef.content) !==
            JSON.stringify(jsonContent)
          : true;
      const shouldAutoSave =
        actualIsToolUpdate &&
        hasChangedRef &&
        jsonContent &&
        JSON.stringify(jsonContent).length > 10;

      const versionForSave = currentDocRef?.version || 1;
      const docNameForSave =
        documentName || currentDocRef?.documentName || "Unknown Document";

      updateContext(prev => {
        const currentDoc = prev.documents[docId];
        if (currentDoc && currentDoc.documentType !== "tiptap") return prev;

        const isCurrentlyDirty =
          currentDoc?.documentType === "tiptap" ? currentDoc.isDirty : false;

        const hasChanged =
          currentDoc?.documentType === "tiptap"
            ? JSON.stringify(currentDoc.content) !== JSON.stringify(jsonContent)
            : true;
        const shouldMarkDirty = hasChanged && !isToolUpdate;

        const textLength = JSON.stringify(jsonContent).length;
        const updatedDoc: TipTapDocument = {
          id: docId,
          documentType: "tiptap",
          documentName:
            documentName || currentDoc?.documentName || "Unknown Document",
          content: jsonContent,
          htmlContent,
          isDirty: shouldMarkDirty ? true : isCurrentlyDirty,
          isLarge: textLength > CONTENT_SIZE_THRESHOLD,
          lastModified: hasChanged
            ? new Date()
            : currentDoc?.lastModified || new Date(),
          version: currentDoc?.version || 1,
          ...(currentDoc?.documentType === "tiptap"
            ? {
                unsavedEditorState: currentDoc.unsavedEditorState,
                comments: currentDoc.comments,
                trackChangesState: currentDoc.trackChangesState,
              }
            : {}),
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: updatedDoc },
        };
      });

      // Auto-save tool updates
      if (shouldAutoSave && htmlContent) {
        const currentComments =
          currentDocRef?.documentType === "tiptap"
            ? (currentDocRef as TipTapDocument).comments
            : undefined;
        autoSaveToolUpdate(
          workproductId,
          docId,
          docNameForSave,
          jsonContent,
          htmlContent,
          versionForSave,
          currentComments
        ).then(result => {
          if (result.status === "ok") {
            adoptServerVersion(docId, result.version);
          } else if (result.status === "conflict") {
            // Adopt the server version so we're not permanently stale, but flag
            // the doc so autosaves stop and the pane prompts a reload/merge.
            adoptServerVersion(docId, result.serverVersion);
            markDocumentConflicted(docId);
          }
        });
      }
    },
    [updateContext, workproductId, adoptServerVersion, markDocumentConflicted]
  );

  const addTipTapDocument = useCallback(
    (
      docId: string,
      documentName: string,
      content: JSONContent,
      editable: boolean = true,
      gcsPath?: string,
      meta?: Record<string, any>,
      htmlContent?: string,
      version?: number,
      comments?: DocumentComment[]
    ) => {
      // Update local state immediately
      updateContext(prev => {
        const tiptapDoc: TipTapDocument = {
          id: docId,
          documentType: "tiptap",
          documentName,
          content,
          htmlContent,
          isDirty: false,
          isLarge: JSON.stringify(content).length > CONTENT_SIZE_THRESHOLD,
          lastModified: new Date(),
          version: version ?? 1,
          gcsPath,
          editable,
          meta,
          comments,
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: tiptapDoc },
        };
      });

      // Persist document metadata to backend (fire-and-forget for new documents without gcsPath)
      // Documents with gcsPath are being loaded from server, not newly created
      if (!gcsPath) {
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
        fetch(`${backendUrl}/api/document/create-document`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            workproductId,
            docId,
            documentName,
            content,
          }),
        })
          .then(response => {
            if (response.ok) {
            } else if (response.status === 409) {
              // Document already exists - this is fine (e.g., page refresh)
            } else {
            }
          })
          .catch(error => {});
      }
    },
    [updateContext, workproductId]
  );

  // ============================================================================
  // TRACK CHANGES
  // ============================================================================

  const setTrackChangesState = useCallback(
    (docId: string, state: DocumentTrackChangesState | null) => {
      updateContext(prev => {
        const doc = prev.documents[docId];
        if (!doc || doc.documentType !== "tiptap") return prev;

        if (state === null) {
          const { trackChangesState, ...rest } = doc;
          return {
            ...prev,
            documents: { ...prev.documents, [docId]: rest as TipTapDocument },
          };
        }

        return {
          ...prev,
          documents: {
            ...prev.documents,
            [docId]: { ...doc, trackChangesState: state },
          },
        };
      });
    },
    [updateContext]
  );

  const getTrackChangesState = useCallback(
    (docId: string): DocumentTrackChangesState | null => {
      const doc = documentContextRef.current.documents[docId];
      if (doc?.documentType === "tiptap" && doc.trackChangesState) {
        return doc.trackChangesState;
      }
      return null;
    },
    []
  );

  const updateTrackChangesState = useCallback(
    (docId: string, updates: Partial<DocumentTrackChangesState>) => {
      updateContext(prev => {
        const doc = prev.documents[docId];
        if (!doc || doc.documentType !== "tiptap" || !doc.trackChangesState)
          return prev;

        return {
          ...prev,
          documents: {
            ...prev.documents,
            [docId]: {
              ...doc,
              trackChangesState: { ...doc.trackChangesState, ...updates },
            },
          },
        };
      });
    },
    [updateContext]
  );

  const clearTrackChangesData = useCallback(
    (docId: string) => {
      setTrackChangesState(docId, null);
    },
    [setTrackChangesState]
  );

  // ============================================================================
  // COMMENT SYNC
  // ============================================================================

  const updateDocumentComments = useCallback(
    (docId: string, comments: DocumentComment[], markDirty: boolean = true) => {
      updateContext(prev => {
        const doc = prev.documents[docId];
        if (!doc || doc.documentType !== "tiptap") return prev;

        // No-op when the comments are unchanged. Without this, the comment-sync
        // effect in TipTapDocumentPane re-runs on every mount/tab switch with
        // identical data and would flip isDirty (lighting the "unsaved" tab
        // dot) even though the user changed nothing.
        // NOTE: this equality relies on both arrays being built with the same
        // key order — they are: every writer maps to DocumentComment via the
        // same shape (the `commentsForContext` mapper in TipTapDocumentPane and
        // the load paths). If a future caller constructs comment objects with a
        // different key order, switch this to a field-by-field compare.
        const prevComments = doc.comments ?? [];
        const unchanged =
          JSON.stringify(prevComments) === JSON.stringify(comments);
        if (unchanged) return prev;

        return {
          ...prev,
          documents: {
            ...prev.documents,
            [docId]: {
              ...doc,
              comments,
              // Only mark dirty for genuine edits. Freshly-loaded comments are
              // synced as a clean baseline (markDirty=false).
              isDirty: markDirty ? true : doc.isDirty,
              lastModified: markDirty ? new Date() : doc.lastModified,
            },
          },
        };
      });
    },
    [updateContext]
  );

  // ============================================================================
  // UNSAVED EDITOR STATE
  // ============================================================================

  const saveUnsavedEditorState = useCallback(
    (docId: string, json: JSONContent, html: string) => {
      updateContext(prev => {
        const existingDoc = prev.documents[docId];
        if (!existingDoc || existingDoc.documentType !== "tiptap") return prev;

        const updatedDoc: TipTapDocument = {
          ...existingDoc,
          unsavedEditorState: { json, html, savedAt: new Date() },
          isDirty: true,
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: updatedDoc },
        };
      });
    },
    [updateContext]
  );

  const restoreUnsavedEditorState = useCallback(
    (docId: string) => {
      const doc = documentContext.documents[docId];
      if (doc?.documentType === "tiptap" && doc.unsavedEditorState) {
        return {
          json: doc.unsavedEditorState.json,
          html: doc.unsavedEditorState.html,
        };
      }
      return null;
    },
    [documentContext.documents]
  );

  const clearUnsavedEditorState = useCallback(
    (docId: string) => {
      updateContext(prev => {
        const existingDoc = prev.documents[docId];
        if (
          !existingDoc ||
          existingDoc.documentType !== "tiptap" ||
          !existingDoc.unsavedEditorState
        ) {
          return prev;
        }

        const { unsavedEditorState, ...rest } = existingDoc;
        return {
          ...prev,
          documents: { ...prev.documents, [docId]: rest as TipTapDocument },
        };
      });
    },
    [updateContext]
  );

  // ============================================================================
  // PDF METHODS
  // ============================================================================

  const addPDFDocument = useCallback(
    (
      docId: string,
      documentName: string,
      pdfGcsPath: string,
      ocrGcsPath: string,
      ocrMetadata: PDFDocument["ocrMetadata"],
      signedPdfUrl?: string
    ) => {
      updateContext(prev => {
        const pdfDoc: PDFDocument = {
          id: docId,
          documentType: "pdf",
          documentName,
          pdfGcsPath,
          ocrGcsPath,
          ocrMetadata,
          signedPdfUrl,
          lastModified: new Date(),
          version: 1,
        };

        const newDocs = { ...prev.documents, [docId]: pdfDoc };

        return {
          ...prev,
          documents: newDocs,
        };
      });
    },
    [updateContext]
  );

  const processPDFUpload = useCallback(
    async (file: File, documentName?: string): Promise<string> => {
      const docId = uuidv4();
      const fileName = documentName || file.name;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("docId", docId);
      formData.append("documentName", fileName);
      formData.append("workproductId", workproductId);

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      const response = await fetch(
        `${backendUrl}/api/document-agent/upload-pdf`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        handleFetchUnauthorized(response);
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      addPDFDocument(
        docId,
        fileName,
        result.pdfGcsPath,
        result.ocrGcsPath,
        result.ocrMetadata
      );

      return docId;
    },
    [addPDFDocument, workproductId]
  );

  // ============================================================================
  // FORM METHODS
  // ============================================================================

  const addFormDocument = useCallback(
    (
      docId: string,
      documentName: string,
      formData: Record<string, any>,
      isMutable: boolean,
      gcsPath?: string,
      formSchema?: string,
      initialDirty: boolean = true,
      meta?: Record<string, any>
    ) => {
      updateContext(prev => {
        let formDoc: FormMutableDocument | FormImmutableDocument;

        if (isMutable) {
          formDoc = {
            id: docId,
            documentType: "form_mutable",
            documentName,
            formData,
            isDirty: initialDirty,
            formSchema,
            lastModified: new Date(),
            version: 1,
            meta,
          };
        } else {
          if (!gcsPath) {
            throw new Error("GCS path required for immutable form documents");
          }
          formDoc = {
            id: docId,
            documentType: "form_immutable",
            documentName,
            gcsPath,
            metadata: { dataType: "form", lastUpdated: new Date() },
            lastModified: new Date(),
            version: 1,
            meta,
          };
        }

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: formDoc },
        };
      });
    },
    [updateContext]
  );

  const updateFormData = useCallback(
    (docId: string, formData: Record<string, any>) => {
      updateContext(prev => {
        const existingDoc = prev.documents[docId];
        if (!existingDoc || existingDoc.documentType !== "form_mutable")
          return prev;

        const updatedDoc: FormMutableDocument = {
          ...existingDoc,
          formData,
          isDirty: true,
          lastModified: new Date(),
        };

        return {
          ...prev,
          documents: { ...prev.documents, [docId]: updatedDoc },
        };
      });
    },
    [updateContext]
  );

  // ============================================================================
  // CONTEXT FOR AGENT
  // ============================================================================

  /**
   * Get document context for the agent.
   *
   * LAZY LOADING: Only sends document metadata (name, type, size hints).
   * Full content is NOT sent — the agent uses view_document, search_documents,
   * and other tools to access content on-demand from the backend (PostgreSQL/GCS).
   *
   * IMPORTANT: saveDirtyDocuments() must be called before this to ensure the
   * backend has the latest content. The chat component already does this.
   */
  const getContextForAgent = useCallback((): DocumentContextData => {
    const metadataDocuments: Record<string, DocumentSnapshot> = {};
    const current = documentContextRef.current;

    Object.entries(current.documents).forEach(([docId, doc]) => {
      switch (doc.documentType) {
        case "tiptap": {
          // Only send metadata — no serialized nodes or content
          const editorRef = editorRefs.current[docId];
          let nodeCount = 0;
          let approximateCharacters = 0;
          let hasComments = false;
          let hasTrackChanges = false;

          let imageCount = 0;

          if (editorRef?.current) {
            // Get lightweight stats without full serialization
            const nodes = editorRef.current.getDocumentForLLM();
            nodeCount = nodes?.length || 0;
            approximateCharacters =
              nodes?.reduce(
                (sum: number, n: SerializedTipTapNode) =>
                  sum + (n.content?.length || 0),
                0
              ) || 0;
            hasComments =
              nodes?.some(
                (n: SerializedTipTapNode) => n.comments && n.comments.length > 0
              ) || false;
            hasTrackChanges =
              nodes?.some(
                (n: SerializedTipTapNode) =>
                  n.trackChanges && n.trackChanges.length > 0
              ) || false;
            imageCount =
              nodes?.filter((n: SerializedTipTapNode) => n.type === "image")
                .length || 0;
          }

          metadataDocuments[docId] = {
            id: docId,
            documentType: "tiptap",
            documentName: doc.documentName,
            isDirty: doc.isDirty,
            isLarge: doc.isLarge,
            lastModified: doc.lastModified,
            version: doc.version,
            // Content is intentionally omitted — agent uses view_document tool
            content: { type: "doc", content: [] },
            meta: {
              // Metadata hints for the agent (no full content)
              nodeCount,
              approximateCharacters,
              hasComments,
              hasTrackChanges,
              imageCount,
              contentOmitted: true, // Signal to backend that content must be loaded
            },
          } as TipTapDocument;
          break;
        }

        case "pdf":
          // PDF metadata only — OCR content loaded by backend from GCS
          metadataDocuments[docId] = {
            ...doc,
            // Strip any cached content, keep paths for backend loading
          };
          break;

        case "form_mutable":
          // Send form metadata only — backend loads from PostgreSQL
          metadataDocuments[docId] = {
            ...doc,
            formData: {}, // Omit full form data
            meta: {
              ...doc.meta,
              fieldCount: Object.keys(
                (doc as FormMutableDocument).formData || {}
              ).length,
              contentOmitted: true,
            },
          } as FormMutableDocument;
          break;

        case "form_immutable":
          metadataDocuments[docId] = doc;
          break;

        default:
          metadataDocuments[docId] = doc;
          break;
      }
    });

    return {
      ...current,
      documents: metadataDocuments,
    };
  }, []);

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  const saveDirtyDocuments = useCallback(async (): Promise<{
    saved: string[];
    failed: string[];
  }> => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
    const currentDocs = documentContextRef.current.documents;
    const tasks: Array<Promise<{ ok: boolean; docId: string }>> = [];

    Object.entries(currentDocs).forEach(([docId, doc]) => {
      // Never autosave a doc with an unresolved version conflict — that would
      // silently overwrite the newer server copy. It stays dirty until the user
      // reloads/merges (which clears the conflict flag).
      if (conflictedDocIdsRef.current.has(docId)) {
        return;
      }
      if (doc.documentType === "tiptap" && doc.isDirty) {
        const tiptapDoc = doc as TipTapDocument;
        // Only save when the editor actually emitted changes this session
        // (unsavedEditorState is written by the editor's onChange). A dirty
        // flag WITHOUT it is a hydration artifact — the context content is
        // a stale workproduct snapshot, and saving it was observed
        // reverting documents (deleting footnotes) with a valid-looking
        // version number that defeats the server's stale-write guard.
        if (!tiptapDoc.unsavedEditorState?.json) {
          return;
        }
        const json = tiptapDoc.unsavedEditorState.json;
        const html =
          tiptapDoc.unsavedEditorState?.html || tiptapDoc.htmlContent || "";

        const attempt = (version: number) =>
          fetch(`${backendUrl}/api/document-agent/save-document`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              workproductId,
              docId,
              documentName: tiptapDoc.documentName,
              documentType: "tiptap",
              document: {
                json,
                html,
                version,
                updated: new Date().toISOString(),
                comments: tiptapDoc.comments,
              },
              changesSummary: "Auto-saved before chat submit",
              meta: { documentName: tiptapDoc.documentName },
            }),
          });

        const task = (async () => {
          try {
            const resp = await attempt((tiptapDoc.version || 0) + 1);
            if (resp.status === 409) {
              // BLOCK & NOTIFY: the server has a newer copy than this client.
              // Do NOT re-send with a bumped version (that silently overwrote
              // the newer content). Adopt the server version so we're not
              // permanently stale, keep the doc dirty, and flag the conflict so
              // the pane prompts a reload/merge and autosaves stop.
              const conflict = await resp.json().catch(() => null);
              const serverVersion = conflict?.currentVersion;
              if (typeof serverVersion === "number") {
                adoptServerVersion(docId, serverVersion);
              }
              markDocumentConflicted(docId);
              return { ok: false, docId };
            }
            if (resp.ok) {
              const respJson = await resp.json().catch(() => null);
              markDocumentClean(docId, json, respJson?.documentMeta?.version);
              clearUnsavedEditorState(docId);
              return { ok: true, docId };
            }
            return { ok: false, docId };
          } catch {
            return { ok: false, docId };
          }
        })();
        tasks.push(task);
      } else if (doc.documentType === "form_mutable" && doc.isDirty) {
        const fd = doc as FormMutableDocument;
        const body = {
          workproductId,
          docId,
          documentName: fd.documentName,
          document: {
            formData: fd.formData,
            version: (fd.version || 0) + 1,
          },
          changesSummary: "Auto-saved form before chat submit",
          meta: {
            documentType: "form_mutable",
            documentName: fd.documentName,
            ...(fd.meta || {}),
          },
        };

        const task = (async () => {
          try {
            const resp = await fetch(
              `${backendUrl}/api/document-agent/save-document`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(body),
              }
            );
            if (resp.ok) {
              updateContext(prev => {
                const existing = prev.documents[docId] as FormMutableDocument;
                if (!existing) return prev;
                return {
                  ...prev,
                  documents: {
                    ...prev.documents,
                    [docId]: {
                      ...existing,
                      isDirty: false,
                      lastModified: new Date(),
                      version: (existing.version || 0) + 1,
                    },
                  },
                };
              });
              return { ok: true, docId };
            }
            return { ok: false, docId };
          } catch {
            return { ok: false, docId };
          }
        })();
        tasks.push(task);
      }
    });

    const results = await Promise.all(tasks);
    return {
      saved: results.filter(r => r.ok).map(r => r.docId),
      failed: results.filter(r => !r.ok).map(r => r.docId),
    };
  }, [
    workproductId,
    markDocumentClean,
    clearUnsavedEditorState,
    updateContext,
    adoptServerVersion,
    markDocumentConflicted,
  ]);

  // ============================================================================
  // GENERAL METHODS
  // ============================================================================

  const isAnyDocumentDirty = useCallback((): boolean => {
    return Object.values(documentContext.documents).some(
      doc =>
        (doc.documentType === "tiptap" && doc.isDirty) ||
        (doc.documentType === "form_mutable" && doc.isDirty)
    );
  }, [documentContext.documents]);

  const getDocumentById = useCallback(
    (docId: string) => documentContext.documents[docId],
    [documentContext.documents]
  );

  const loadDocumentById = useCallback(
    async (docId: string): Promise<boolean> => {
      try {
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

        const response = await fetch(
          `${backendUrl}/api/document/get-document`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ workproductId, docId }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.document) {
            const loadedDoc: TipTapDocument = {
              id: docId,
              documentType: "tiptap",
              documentName:
                data.documentMeta?.documentName || "Unknown Document",
              content: data.document.json || { type: "doc", content: [] },
              htmlContent: data.document.html,
              isDirty: false,
              isLarge:
                JSON.stringify(data.document.json || {}).length >
                CONTENT_SIZE_THRESHOLD,
              lastModified: new Date(),
              version: data.documentMeta?.version || 1,
              // Include comments if present
              comments: data.document.comments || [],
            };

            updateContext(prev => ({
              ...prev,
              documents: { ...prev.documents, [docId]: loadedDoc },
            }));
            return true;
          }
        }
        return false;
      } catch {
        return false;
      }
    },
    [workproductId, updateContext]
  );

  const getAvailableDocuments = useCallback(() => {
    return Object.entries(documentContext.documents).map(([docId, doc]) => ({
      docId,
      documentName: doc.documentName,
      documentType: doc.documentType,
    }));
  }, [documentContext.documents]);

  const renameDocument = useCallback(
    async (docId: string, newName: string): Promise<boolean> => {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      // Update local state immediately for optimistic UI
      updateContext(prev => {
        const doc = prev.documents[docId];
        if (!doc) return prev;

        return {
          ...prev,
          documents: {
            ...prev.documents,
            [docId]: { ...doc, documentName: newName },
          },
        };
      });

      // Persist to backend
      try {
        const response = await fetch(
          `${backendUrl}/api/document/rename-document`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ workproductId, docId, newName }),
          }
        );

        if (!response.ok) {
          if (handleFetchUnauthorized(response)) return false;
          return false;
        }

        return true;
      } catch (error) {
        console.error("Error renaming document:", error);
        return false;
      }
    },
    [updateContext, workproductId]
  );

  const removeDocument = useCallback(
    async (docId: string): Promise<boolean> => {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      // Unregister editor first if exists
      unregisterEditor(docId);

      // Remove from local state immediately for optimistic UI
      updateContext(prev => {
        const newDocuments = { ...prev.documents };
        delete newDocuments[docId];
        return {
          ...prev,
          documents: newDocuments,
        };
      });

      // Persist to backend
      try {
        const response = await fetch(
          `${backendUrl}/api/document/delete-document`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ workproductId, docId }),
          }
        );

        if (!response.ok) {
          if (handleFetchUnauthorized(response)) return false;
          return false;
        }

        return true;
      } catch (error) {
        console.error("Error deleting document:", error);
        return false;
      }
    },
    [updateContext, workproductId, unregisterEditor]
  );

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // Warn user and auto-save dirty documents on page unload to prevent data loss
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentDocs = documentContextRef.current.documents;
      const hasDirtyDocs = Object.values(currentDocs).some(
        doc =>
          (doc.documentType === "tiptap" && doc.isDirty) ||
          (doc.documentType === "form_mutable" && doc.isDirty)
      );

      // Show browser confirmation dialog if there are unsaved changes
      if (hasDirtyDocs) {
        e.preventDefault();
      }

      // Also attempt to auto-save dirty tiptap documents via sendBeacon
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      Object.entries(currentDocs).forEach(([docId, doc]) => {
        if (doc.documentType === "tiptap" && doc.isDirty) {
          const tiptapDoc = doc as TipTapDocument;
          // Same stale-snapshot guard as saveDirtyDocuments: without
          // editor-emitted state, the context content is a hydration
          // snapshot and saving it can revert the document.
          if (!tiptapDoc.unsavedEditorState?.json) {
            return;
          }
          const json = tiptapDoc.unsavedEditorState.json;
          const html =
            tiptapDoc.unsavedEditorState?.html || tiptapDoc.htmlContent || "";
          const body = JSON.stringify({
            workproductId,
            docId,
            documentName: tiptapDoc.documentName,
            documentType: "tiptap",
            document: {
              json,
              html,
              version: (tiptapDoc.version || 0) + 1,
              updated: new Date().toISOString(),
              comments: tiptapDoc.comments,
            },
            changesSummary: "Auto-saved on page close",
            meta: { documentName: tiptapDoc.documentName },
          });
          // Use sendBeacon for reliable delivery during page unload
          navigator.sendBeacon(
            `${backendUrl}/api/document-agent/save-document`,
            new Blob([body], { type: "application/json" })
          );
        }
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [workproductId]);

  const contextValue: DocumentContextValue = {
    documentContext,
    documentReadOnly,
    markDocumentDirty,
    markDocumentClean,
    updateDocumentContent,
    addTipTapDocument,
    addPDFDocument,
    processPDFUpload,
    addFormDocument,
    updateFormData,
    getContextForAgent,
    isAnyDocumentDirty,
    getDocumentById,
    loadDocumentById,
    getAvailableDocuments,
    renameDocument,
    removeDocument,
    setSelectedDocument,
    setVisibleDocuments,
    setTrackChangesState,
    getTrackChangesState,
    updateTrackChangesState,
    clearTrackChangesData,
    saveUnsavedEditorState,
    restoreUnsavedEditorState,
    clearUnsavedEditorState,
    saveDirtyDocuments,
    conflictedDocIds,
    markDocumentConflicted,
    clearDocumentConflict,
    updateDocumentComments,
    registerEditor,
    unregisterEditor,
    applyPatch,
    getSerializedDocument,
    registerCommentCallback,
    unregisterCommentCallback,
    isAgentWorking,
    setAgentWorking,
  };

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
};

// Export alias for compatibility

export default DocumentContextProvider;
